import { DiffLineKind } from '../../../../../api/agent-miner-client/agent-miner-client.type';
import {
  baseWhite,
  inkDark,
  primaryLightest,
  redLightest,
  secondaryBase,
  secondaryDarkest,
  secondaryLight,
  secondaryLighter,
  secondaryLightest,
  skyDark,
} from '../../../../../styles/design-tokens';
import { turnDisplayNumber } from '../../../shared/formatters';
import { sessionFallbackTitle } from '../pr-detail.helpers';
import type { PullRequestDiffLine } from '../../../../../api/agent-miner-client/agent-miner-client.type';
import type { LineAttributionDisplayKind } from '../pr-survival.helpers';
import type { TFunction } from 'i18next';

/**
 * Builds the human-readable tooltip and screen-reader text for an attributed
 * line, naming the turn and session and calling out ambiguous or file-created
 * matches.
 * @param {PullRequestDiffLine} line - The diff line to describe.
 * @param {TFunction} t - The translation function.
 * @returns {string} The attribution description, or an empty string when the line carries no attribution.
 */
const describeAttribution = (line: PullRequestDiffLine, t: TFunction): string => {
  const attribution = line.attribution;
  if (!attribution) return '';
  const turn = turnDisplayNumber(attribution.turn_number ?? 0);
  const session = attribution.session_title ?? sessionFallbackTitle(attribution.session_id ?? '');
  if (attribution.match_kind === 'ambiguous') {
    return String(t('agent_miner.pull_requests.code.tag_tooltip_ambiguous', { turn, session }));
  }
  if (attribution.match_kind === 'file_created') {
    return String(t('agent_miner.pull_requests.code.tag_tooltip_file_created', { turn, session }));
  }
  return String(t('agent_miner.pull_requests.code.tag_tooltip', { turn, session }));
};

/**
 * Background colour for a diff row: the secondary tint for agent-attributed
 * lines, the add/delete tints for unattributed changes, white for context.
 * @param {PullRequestDiffLine['kind']} kind - The diff line kind.
 * @param {LineAttributionDisplayKind} attribution - The line's attribution bucket.
 * @returns {string} A design-token colour.
 */
const rowBackground = (kind: PullRequestDiffLine['kind'], attribution: LineAttributionDisplayKind): string => {
  if (attribution === 'agent-current') return secondaryLightest;
  if (attribution === 'agent-other') return `${secondaryLightest}66`;
  if (attribution === 'agent-ambiguous') return `${secondaryLightest}66`;
  if (kind === DiffLineKind.Add) return primaryLightest;
  if (kind === DiffLineKind.Del) return redLightest;
  return baseWhite;
};

/**
 * Left-border for a diff row: a solid accent for the current session, a lighter
 * solid for other sessions, a dashed accent for ambiguous matches, transparent
 * otherwise.
 * @param {LineAttributionDisplayKind} attribution - The line's attribution bucket.
 * @returns {string} A CSS border shorthand.
 */
const rowBorder = (attribution: LineAttributionDisplayKind): string => {
  if (attribution === 'agent-current') return `3px solid ${secondaryBase}`;
  if (attribution === 'agent-other') return `3px solid ${secondaryLighter}`;
  if (attribution === 'agent-ambiguous') return `3px dashed ${secondaryLight}`;
  return '3px solid transparent';
};

/**
 * Sign-gutter colour for a diff line: green for additions, ink for deletions,
 * muted for context.
 * @param {PullRequestDiffLine['kind']} kind - The diff line kind.
 * @returns {string} A design-token colour.
 */
const signColor = (kind: PullRequestDiffLine['kind']): string => {
  if (kind === DiffLineKind.Add) return secondaryDarkest;
  if (kind === DiffLineKind.Del) return inkDark;
  return skyDark;
};

/**
 * Background colour for an attribution tag: white for ambiguous matches (paired
 * with a dashed border), the secondary tint otherwise.
 * @param {boolean} ambiguous - Whether the match is ambiguous.
 * @returns {string} A design-token colour.
 */
const tagBackground = (ambiguous: boolean): string => (ambiguous ? baseWhite : secondaryLightest);

/**
 * Border style for an attribution tag: dashed for ambiguous matches, solid
 * otherwise.
 * @param {boolean} ambiguous - Whether the match is ambiguous.
 * @returns {'dashed' | 'solid'} The CSS border style.
 */
const tagBorderStyle = (ambiguous: boolean): 'dashed' | 'solid' => (ambiguous ? 'dashed' : 'solid');

export { describeAttribution, rowBackground, rowBorder, signColor, tagBackground, tagBorderStyle };
